import React, { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { jsPDF } from 'jspdf';
import type { ObjectivePower } from './magnification';
import type { LightingSettings } from './lighting';
import type { SpecimenSlide } from './specimens';
import AnnotationCanvas from './AnnotationCanvas';

export interface MicroscopyObservation {
  _id?: string;
  slideId: string;
  slideName: string;
  objective: ObjectivePower;
  eyepiece: number;
  lamp: number;
  diaphragm: number;
  condenser: number;
  structuresFound: string[];
  description: string;
  snapshotDataUrl: string;
  annotationDataUrl: string;
  measurementMicrometres?: number;
  measurementUncertaintyMicrometres?: number;
  timestamp: string;
}

interface NotebookProps {
  observations: MicroscopyObservation[];
  slide: SpecimenSlide;
  objective: ObjectivePower;
  lighting: LightingSettings;
  measurement: { distanceMicrometres: number; uncertaintyMicrometres: number } | null;
  snapshotDraft: string;
  conclusion: string;
  onConclusionChange: (value: string) => void;
  onAdd: (observation: MicroscopyObservation) => void;
  onUpdate: (observationId: string, patch: Partial<MicroscopyObservation>) => void;
  onDelete: (observationId: string) => void;
  onReportGenerated: (pdfDataUrl: string) => void;
}

function escapeCsv(value: string): string { return `"${value.replace(/"/g, '""')}"`; }

const Notebook: React.FC<NotebookProps> = ({ observations, slide, objective, lighting, measurement, snapshotDraft, conclusion, onConclusionChange, onAdd, onUpdate, onDelete, onReportGenerated }) => {
  const [selectedStructures, setSelectedStructures] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [annotationTarget, setAnnotationTarget] = useState<MicroscopyObservation | null>(null);
  const [feedback, setFeedback] = useState('');

  const addObservation = () => {
    if (!snapshotDraft && !description.trim() && selectedStructures.length === 0) {
      setFeedback('Add a note, identify a structure, or capture a snapshot first.');
      return;
    }
    onAdd({
      slideId: slide.id, slideName: slide.name, objective, eyepiece: 10,
      lamp: lighting.lamp, diaphragm: lighting.diaphragm, condenser: lighting.condenser,
      structuresFound: selectedStructures, description: description.trim(), snapshotDataUrl: snapshotDraft,
      annotationDataUrl: '', measurementMicrometres: measurement?.distanceMicrometres,
      measurementUncertaintyMicrometres: measurement?.uncertaintyMicrometres, timestamp: new Date().toISOString(),
    });
    setSelectedStructures([]); setDescription(''); setFeedback('Observation added to notebook.');
  };

  const exportCsv = () => {
    const headings = ['Timestamp', 'Slide', 'Objective', 'Total magnification', 'Lamp %', 'Diaphragm %', 'Condenser %', 'Structures', 'Measurement µm', 'Uncertainty µm', 'Description'];
    const rows = observations.map((entry) => [entry.timestamp, entry.slideName, `${entry.objective}×`, `${entry.objective * entry.eyepiece}×`, `${Math.round(entry.lamp * 100)}`, `${Math.round(entry.diaphragm * 100)}`, `${Math.round(entry.condenser * 100)}`, entry.structuresFound.join('; '), entry.measurementMicrometres?.toFixed(2) || '', entry.measurementUncertaintyMicrometres?.toFixed(2) || '', entry.description]);
    const csv = [headings, ...rows].map((row) => row.map((value) => escapeCsv(String(value))).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a'); link.href = url; link.download = 'microscopy-observations.csv'; link.click(); URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const document = new jsPDF({ unit: 'mm', format: 'a4' });
    let y = 18;
    document.setFontSize(19); document.text('Virtual Microscopy Laboratory Report', 14, y); y += 11;
    document.setFontSize(10); document.text(`Student: ${localStorage.getItem('userName') || 'Registered student'}`, 14, y); y += 6;
    document.text(`Date: ${new Date().toLocaleString()} · Observations: ${observations.length}`, 14, y); y += 10;
    observations.forEach((entry, index) => {
      if (y > 250) { document.addPage(); y = 18; }
      document.setFontSize(13); document.text(`${index + 1}. ${entry.slideName} — ${entry.objective * entry.eyepiece}×`, 14, y); y += 6;
      document.setFontSize(10);
      const lines = document.splitTextToSize(`Structures: ${entry.structuresFound.join(', ') || 'none recorded'}\nSettings: lamp ${Math.round(entry.lamp * 100)}%, iris ${Math.round(entry.diaphragm * 100)}%, condenser ${Math.round(entry.condenser * 100)}%\nMeasurement: ${entry.measurementMicrometres?.toFixed(1) ?? '—'} ± ${entry.measurementUncertaintyMicrometres?.toFixed(1) ?? '—'} µm\nNotes: ${entry.description || '—'}`, 180);
      document.text(lines, 14, y); y += lines.length * 5 + 3;
      const image = entry.annotationDataUrl || entry.snapshotDataUrl;
      if (image && y < 220) { try { document.addImage(image, 'JPEG', 14, y, 72, 72); y += 77; } catch (error) { console.warn('Could not add notebook image to report', error); } }
    });
    if (y > 250) { document.addPage(); y = 18; }
    document.setFontSize(13); document.text('Conclusion', 14, y); y += 7;
    document.setFontSize(10); document.text(document.splitTextToSize(conclusion || 'No conclusion entered.', 180), 14, y);
    onReportGenerated(document.output('datauristring'));
    document.save('microscopy-lab-report.pdf');
  };

  return (
    <Paper className="micro-panel notebook-panel" elevation={0}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
        <Box><Typography variant="h6" fontWeight={800}>Digital lab notebook</Typography><Typography variant="body2" color="text.secondary">{observations.length} saved observation{observations.length === 1 ? '' : 's'} · account autosave enabled</Typography></Box>
        <Stack direction="row" spacing={1}><Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!observations.length}>CSV</Button><Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={exportPdf}>Export PDF</Button></Stack>
      </Stack>
      {feedback && <Alert severity="info" sx={{ mt: 1 }} onClose={() => setFeedback('')}>{feedback}</Alert>}
      <Box className="notebook-entry-form">
        <Typography variant="subtitle1" fontWeight={700}>Record current view · {slide.name} · {objective * 10}×</Typography>
        {snapshotDraft ? <img className="notebook-snapshot-preview" src={snapshotDraft} alt={`Current ${slide.name} microscope field snapshot`} /> : <Typography variant="caption" color="text.secondary">Capture an eyepiece snapshot above to include an image.</Typography>}
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>Structures identified</Typography>
        <div className="notebook-structure-list">
          {slide.structures.map((structure) => <FormControlLabel key={structure} control={<Checkbox size="small" checked={selectedStructures.includes(structure)} onChange={(event) => setSelectedStructures((current) => event.target.checked ? [...current, structure] : current.filter((item) => item !== structure))} />} label={structure} />)}
        </div>
        <TextField fullWidth multiline minRows={2} label="Observation notes" value={description} onChange={(event) => setDescription(event.target.value)} />
        {measurement && <Typography variant="body2">Current size estimate: {measurement.distanceMicrometres.toFixed(1)} ± {measurement.uncertaintyMicrometres.toFixed(1)} µm</Typography>}
        <Button variant="contained" onClick={addObservation}>Add observation</Button>
      </Box>
      <TextField fullWidth multiline minRows={3} label="Conclusion" value={conclusion} onChange={(event) => onConclusionChange(event.target.value)} sx={{ mt: 2 }} />
      <Stack spacing={1.2} sx={{ mt: 2 }}>
        {observations.map((entry) => {
          const id = entry._id || entry.timestamp;
          return <Paper className="notebook-record" key={id} variant="outlined">
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box sx={{ minWidth: 0 }}><Typography fontWeight={800}>{entry.slideName} · {entry.objective * entry.eyepiece}×</Typography><Typography variant="caption" color="text.secondary">{new Date(entry.timestamp).toLocaleString()} · lamp {Math.round(entry.lamp * 100)}% · iris {Math.round(entry.diaphragm * 100)}%</Typography>
                {entry.structuresFound.length > 0 && <Typography variant="body2" sx={{ mt: .5 }}>Identified: {entry.structuresFound.join(', ')}</Typography>}
                <TextField fullWidth size="small" multiline minRows={1} value={entry.description} onChange={(event) => onUpdate(id, { description: event.target.value })} placeholder="Add or edit notes" sx={{ mt: 1 }} />
                {entry.measurementMicrometres !== undefined && <Typography variant="body2">Size: {entry.measurementMicrometres.toFixed(1)} ± {(entry.measurementUncertaintyMicrometres || 0.5).toFixed(1)} µm</Typography>}
                {(entry.annotationDataUrl || entry.snapshotDataUrl) && <img className="notebook-snapshot-preview" src={entry.annotationDataUrl || entry.snapshotDataUrl} alt={`Notebook snapshot: ${entry.slideName}`} />}
              </Box>
              <Stack direction="row"><Tooltip title="Draw or label this snapshot"><span><IconButton disabled={!entry.snapshotDataUrl} onClick={() => setAnnotationTarget(entry)} aria-label="Annotate snapshot"><EditOutlinedIcon /></IconButton></span></Tooltip><Tooltip title="Delete observation"><IconButton onClick={() => onDelete(id)} aria-label="Delete observation"><DeleteOutlineIcon /></IconButton></Tooltip></Stack>
            </Stack>
          </Paper>;
        })}
      </Stack>
      <Dialog open={Boolean(annotationTarget)} onClose={() => setAnnotationTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle>Annotate microscope snapshot</DialogTitle>
        <DialogContent>{annotationTarget?.snapshotDataUrl && <AnnotationCanvas snapshotDataUrl={annotationTarget.snapshotDataUrl} onSave={(annotationDataUrl) => { onUpdate(annotationTarget._id || annotationTarget.timestamp, { annotationDataUrl }); setAnnotationTarget(null); }} />}</DialogContent>
        <DialogActions><Button onClick={() => setAnnotationTarget(null)}>Close</Button></DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Notebook;
